import { useState, memo, useLayoutEffect, useRef } from "react";

import { Input, Combobox, Button } from "@newsela/angelou";
import { isEmpty, isNil } from "@s-libs/micro-dash";
import PropTypes from "prop-types";

import AddIcon from "@/icons/AddIcon";
import US_STATES from "@/constants/US_STATES";
import {
  gridStyles,
  gridColumnx1Styles,
  gridColumnx3Styles,
  gridColumnx4Styles,
  gridColumnFlexStyles,
  inlineDivStyles,
  comboboxStyles,
  subtitleStyles,
  addDomainButtonStyles,
  updateInformationButtonStyles,
  zipInputStyles,
} from "./AdminConsoleSchoolGeneralInformationForm.style";
import {
  FORM_ERROR_MESSAGES,
  isFormValid,
  mapFormValues,
  readOnlyFieldMessage,
  schoolStatusValue,
  isEmailValid,
  isWebsiteValid,
  INITIAL_FORM_STATE,
} from "./AdminConsoleSchoolGeneralInformationForm.utils";
import AdminConsoleSchoolGeneralInformationValidationModal from "./AdminConsoleSchoolGeneralInformationValidationModal/AdminConsoleSchoolGeneralInformationValidationModal";

const getFormValues = (data = {}) =>
  Object.entries(data).reduce((prev, [key, value]) => {
    prev[key] = value?.value;

    return prev;
  }, {});

const areStatesEqual = (originalState, newState) =>
  originalState?.abbreviation === newState?.abbreviation &&
  originalState?.name === newState?.name;

const areDomainsEqual = (originalDomains = [], newDomains = []) => {
  return (
    originalDomains.map(({ domain }) => domain).join(",") ===
    newDomains.map(({ domain }) => domain).join(",")
  );
};

const isFormDirty = (originalValues, formValues) => {
  const original = getFormValues(originalValues);
  const newValues = getFormValues(formValues);
  let isDataEqual = true;

  const { emailDomains, state, ...simpleData } = original;

  isDataEqual = Object.entries(simpleData).every(([key, value]) => {
    const areFieldsEmpty = isEmpty(value) && isEmpty(newValues[key]);

    return value === newValues[key] || areFieldsEmpty;
  });

  if (isDataEqual) {
    isDataEqual =
      areStatesEqual(state, newValues?.state) &&
      areDomainsEqual(emailDomains, newValues?.emailDomains);
  }

  return !isDataEqual;
};

const propTypes = {
  title: PropTypes.string,
  containerWidth: PropTypes.number,
  standalone: PropTypes.bool,
  values: PropTypes.object,
  // Check that the school has a product license
  schoolIsLicensed: PropTypes.bool,
  onUpdate: PropTypes.func,
  onValidate: PropTypes.func,
  schoolSource: PropTypes.string,
  onChange: PropTypes.func,
  canEdit: PropTypes.bool,
};

const AdminConsoleSchoolGeneralInformationForm = ({
  title,
  containerWidth,
  standalone,
  onUpdate,
  onValidate,
  schoolSource,
  onChange,
  values = INITIAL_FORM_STATE,
  schoolIsLicensed = false,
  canEdit = false,
}) => {
  const [formValues, setFormValues] = useState(undefined);
  const [shouldFocusDomain, setShouldFocusDomain] = useState(false);
  const lastDomainRef = useRef();
  const gridColumnStyles = standalone ? gridColumnx4Styles : gridColumnx3Styles;
  const isSubmitButtonDisabled =
    !formValues?.isValid || !isFormDirty(values, formValues);
  const showReviewStatusAction =
    schoolSource === "Newsela" && isNil(formValues?.status?.value);

  const updateFormState = (newFormState) => {
    setFormValues(newFormState);
    if (!onChange) return;
    onChange(newFormState);
  };

  useLayoutEffect(() => {
    setFormValues(mapFormValues(values));
  }, [values]);

  useLayoutEffect(() => {
    if (!shouldFocusDomain) return;
    lastDomainRef.current.focus();
    setShouldFocusDomain(false);
  }, [shouldFocusDomain]);

  const updateState = (name, newFormValues, currentFormValues) => {
    const { value, error, touched } = newFormValues;

    const newFormState = {
      ...currentFormValues,
      [name]: {
        ...currentFormValues[name],
        value,
        error,
        touched,
        disabled: currentFormValues[name].disabled,
      },
    };
    newFormState.isValid = isFormValid(newFormState);
    updateFormState(newFormState);
  };

  const handleEmailDomainInputChange = (event, index) => {
    const { name, value } = event.target;
    const { value: domains, touched } = formValues[name];
    const error =
      (value && isEmailValid(value) && touched) || !value
        ? null
        : FORM_ERROR_MESSAGES[name];
    // update domain value that changed @ the email domains list.
    const newDomain = { domain: value, error };
    const newDomains = domains.map((domain, key) =>
      key === index ? newDomain : domain,
    );
    // trigger form state updates.
    const newFormState = {
      ...formValues,
      [name]: {
        ...formValues[name],
        value: newDomains,
        touched: true,
        disabled: formValues[name].disabled,
      },
    };
    newFormState.isValid = isFormValid(newFormState);
    updateFormState(newFormState);
  };

  const handleSchoolWebsiteInputChange = (event) => {
    const { name, value } = event.target;
    const error =
      (value && isWebsiteValid(value)) || !value
        ? null
        : FORM_ERROR_MESSAGES[name];

    updateState(name, { value, error, touched: true }, formValues);
  };

  const handleAddAnotherEmailDomain = () => {
    const { value: domains } = formValues.emailDomains;
    const newFormState = {
      ...formValues,
      emailDomains: {
        ...formValues.emailDomains,
        value: [
          ...domains,
          {
            domain: "",
            // TODO: define how domain id should be set to add a new school domain.
          },
        ],
      },
    };
    updateFormState(newFormState);
    setShouldFocusDomain(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const inputError = value ? null : FORM_ERROR_MESSAGES[name];

    updateState(name, { value, error: inputError, touched: true }, formValues);
  };

  const handleOnBlur = (inputName) => {
    if (formValues[inputName].touched) return;
    const newFormState = {
      ...formValues,
      [inputName]: {
        ...formValues[inputName],
        touched: true,
        error: !formValues[inputName].value && FORM_ERROR_MESSAGES[inputName],
      },
    };
    updateFormState(newFormState);
  };

  const getStatusValues = () => {
    if (isNil(formValues?.status?.value)) {
      return {
        value: schoolStatusValue(formValues?.status?.value),
        disabled: true,
      };
    }
    return {
      value: schoolStatusValue(formValues?.status?.value),
      disabled: true,
      helperText: readOnlyFieldMessage(true),
    };
  };

  return (
    <>
      <section>
        <form role="form" aria-label="School general information">
          {title && <h2 css={subtitleStyles}>{title}</h2>}
          <div css={gridStyles(standalone ? 8 : 6, containerWidth)}>
            <div css={gridColumnStyles}>
              <Input
                name="schoolName"
                label="School name"
                placeholder="Start typing..."
                value={formValues?.schoolName?.value}
                touched={formValues?.schoolName?.touched}
                error={formValues?.schoolName?.error}
                disabled={formValues?.schoolName?.disabled}
                helperText={readOnlyFieldMessage(
                  formValues?.schoolName?.disabled,
                )}
                onChange={handleInputChange}
                onBlur={() => handleOnBlur(formValues?.schoolName?.name)}
                maxLength={50}
              />
            </div>
            <div css={gridColumnStyles}>
              {standalone ? (
                <Input
                  name="website"
                  label="School website"
                  placeholder="Start typing..."
                  value={formValues?.website?.value}
                  error={formValues?.website?.error}
                  isRequired={false}
                  touched={formValues?.website?.touched}
                  onChange={handleSchoolWebsiteInputChange}
                  maxLength={50}
                />
              ) : (
                <Input
                  name="parentDistrict"
                  label="Parent district"
                  value={formValues?.parentDistrict?.value}
                  touched={formValues?.parentDistrict?.touched}
                  error={formValues?.parentDistrict?.error}
                  onChange={handleInputChange}
                  onBlur={() => handleOnBlur(formValues?.parentDistrict?.name)}
                  maxLength={50}
                  disabled // TODO: Review is this should be editable in some scenarios ?
                  helperText={readOnlyFieldMessage(true)}
                />
              )}
            </div>
            <div css={gridColumnStyles}>
              <Input
                name="streetAddress"
                label="Street address"
                placeholder="Start typing..."
                value={formValues?.streetAddress?.value}
                touched={formValues?.streetAddress?.touched}
                error={formValues?.streetAddress?.error}
                disabled={formValues?.streetAddress?.disabled}
                helperText={readOnlyFieldMessage(
                  formValues?.streetAddress?.disabled,
                )}
                onChange={handleInputChange}
                onBlur={() => handleOnBlur(formValues?.streetAddress?.name)}
                maxLength={50}
              />
            </div>
            <div css={gridColumnStyles}>
              <Input
                name="suite"
                label="Suite / Other"
                placeholder="Start typing..."
                value={formValues?.suite?.value}
                disabled={formValues?.suite?.disabled}
                helperText={readOnlyFieldMessage(formValues?.suite?.disabled)}
                isRequired={false}
                onChange={handleInputChange}
                maxLength={50}
              />
            </div>
            <div css={gridColumnStyles}>
              <Input
                name="city"
                label="City"
                placeholder="Start typing..."
                value={formValues?.city?.value}
                touched={formValues?.city?.touched}
                error={formValues?.city?.error}
                disabled={formValues?.city?.disabled}
                helperText={readOnlyFieldMessage(formValues?.city?.disabled)}
                onChange={handleInputChange}
                onBlur={() => handleOnBlur(formValues?.city?.name)}
                maxLength={50}
                __cssFor={inlineDivStyles}
              />
            </div>
            <div css={[gridColumnStyles, gridColumnFlexStyles]}>
              <Combobox
                name="state"
                label="State"
                items={US_STATES}
                itemToString={(item) => item?.name || ""}
                selectedItem={formValues?.state?.value}
                touched={formValues?.state?.touched}
                error={formValues?.state?.error}
                helperText={readOnlyFieldMessage(formValues?.state?.disabled)}
                toggleButtonProps={{
                  disabled: formValues?.state?.disabled,
                }}
                createInputProps={() => ({
                  onBlur: () => handleOnBlur(formValues?.state?.name),
                  placeholder: "Select a state...",
                  disabled: formValues?.state?.disabled,
                })}
                onSelectedItemChange={({ selectedItem }) =>
                  handleInputChange({
                    target: {
                      name: "state",
                      value: selectedItem,
                    },
                  })
                }
                __cssFor={{
                  root: comboboxStyles,
                }}
              />
              <Input
                name="zip"
                label="ZIP code"
                placeholder="Enter zip..."
                value={formValues?.zip?.value}
                touched={formValues?.zip?.touched}
                error={formValues?.zip?.error}
                disabled={formValues?.zip?.disabled}
                helperText={readOnlyFieldMessage(formValues?.zip?.disabled)}
                onChange={handleInputChange}
                onBlur={() => handleOnBlur(formValues?.zip?.name)}
                maxLength={10}
                __cssFor={{ ...zipInputStyles, ...inlineDivStyles }}
              />
            </div>
          </div>
        </form>
      </section>
      {!standalone && (
        <>
          <section>
            <form role="form" aria-label="School domain information">
              <div
                css={
                  showReviewStatusAction
                    ? gridStyles(7, 835)
                    : gridStyles(6, containerWidth)
                }
              >
                <div css={gridColumnx3Styles}>
                  <Input
                    name="website"
                    label="School website"
                    placeholder="Start typing..."
                    value={formValues?.website?.value}
                    disabled={formValues?.website?.disabled}
                    touched={formValues?.website?.touched}
                    helperText={readOnlyFieldMessage(
                      formValues?.website?.disabled,
                    )}
                    isRequired={false}
                    error={formValues?.website?.error}
                    onChange={handleSchoolWebsiteInputChange}
                    maxLength={50}
                  />
                </div>
                <div css={gridColumnx3Styles}>
                  <Input name="status" label="Status" {...getStatusValues()} />
                </div>
                {showReviewStatusAction && (
                  <div css={gridColumnx1Styles}>
                    <AdminConsoleSchoolGeneralInformationValidationModal
                      onSubmit={onValidate}
                      canEdit={canEdit}
                      schoolIsLicensed={schoolIsLicensed}
                    />
                  </div>
                )}
                <div css={gridColumnx3Styles}>
                  {formValues?.emailDomains?.value?.map((value, index) => {
                    const { domain, error } = value;

                    const label = index === 0 ? "Email domains" : "";
                    const showOptionalRequiredLabel = !(index === 0);
                    return (
                      <Input
                        name="emailDomains"
                        placeholder="No email domain provided"
                        key={index}
                        label={label}
                        value={domain}
                        disabled={formValues?.emailDomains?.disabled}
                        error={error}
                        touched={formValues?.emailDomains?.touched}
                        isRequired={showOptionalRequiredLabel}
                        maxLength={50}
                        onChange={(event) =>
                          handleEmailDomainInputChange(event, index)
                        }
                        helperText={readOnlyFieldMessage(
                          formValues?.emailDomains?.disabled,
                        )}
                        inputAriaProps={
                          index > 0
                            ? {
                                "aria-label": `Additional email domain ${index}`,
                              }
                            : undefined
                        }
                        ref={
                          formValues.emailDomains.value.length - 1 === index
                            ? lastDomainRef
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
                {!formValues?.emailDomains?.disabled && (
                  <div css={[gridColumnx3Styles, gridColumnFlexStyles]}>
                    <Button
                      flavor={Button.flavors.tertiary}
                      onClick={handleAddAnotherEmailDomain}
                      __cssFor={addDomainButtonStyles}
                      icon={{
                        SvgComponent: AddIcon,
                        alignment: Button.iconAlignments.left,
                      }}
                    >
                      Add another domain
                    </Button>
                  </div>
                )}
              </div>
            </form>
          </section>
          <Button
            flavor={Button.flavors.primary}
            disabled={isSubmitButtonDisabled}
            onClick={() => onUpdate(formValues)}
            __cssFor={updateInformationButtonStyles}
          >
            Update profile
          </Button>
        </>
      )}
    </>
  );
};

AdminConsoleSchoolGeneralInformationForm.propTypes = propTypes;

export default memo(AdminConsoleSchoolGeneralInformationForm);
